import { authBearer, PortalApiError, portalApiPost, portalApiGet } from '@/lib/portal-api';
import { getSupabaseBrowserClient } from '@/lib/supabase';

export interface PendingBillMutation { path: string; payload: Record<string, unknown>; operationId: string }
/** One unfinished bill operation per authenticated browser session. Its body
 * is preserved before POST and never replaced after an ambiguous response. */
export class PendingBillMutationStore {
  constructor(private storage: Pick<Storage,'getItem'|'setItem'|'removeItem'>, readonly key: string) {}
  read(): PendingBillMutation | null {
    const raw=this.storage.getItem(this.key);
    if(raw===null)return null;
    const value=JSON.parse(raw) as PendingBillMutation;
    if(!value || typeof value.path!=='string' || !(value.path==='/patient-bills' || /^\/(patient-bills\/[1-9]\d*\/(items(?:\/[1-9]\d*\/(update|delete))?|gst|save|finalize|cancel)|patient-payments\/[1-9]\d*\/void)$/.test(value.path)) ||
      typeof value.operationId!=='string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.operationId) || !value.payload || typeof value.payload!=='object' || Array.isArray(value.payload)) {
      throw new Error('The unconfirmed bill change could not be read.');
    }
    return value;
  }
  begin(path: string,payload: Record<string, unknown>): PendingBillMutation {
    const pending=this.read();
    if(pending) {
      if(pending.path!==path || JSON.stringify(pending.payload)!==JSON.stringify(payload)) throw new Error('Resolve the unconfirmed bill change before making another change.');
      return pending;
    }
    const next={path,payload,operationId:crypto.randomUUID()};
    this.storage.setItem(this.key,JSON.stringify(next));
    if(JSON.stringify(this.read())!==JSON.stringify(next))throw new Error('Bill recovery could not be saved. Nothing was sent.');
    return next;
  }
  complete(id: string) { if(this.read()?.operationId===id)this.storage.removeItem(this.key); }
}
async function currentStore(kind: 'bill' | 'void' = 'bill'): Promise<PendingBillMutationStore> {
  const client=getSupabaseBrowserClient();
  let actor=(await client?.auth.getSession())?.data.session?.user.id ?? null;
  if(!actor && !client && import.meta.env.DEV) {
    const token=await authBearer();
    if(token)actor=Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(token))),b=>b.toString(16).padStart(2,'0')).join('');
  }
  if(!actor)throw new Error('Sign in before changing the bill.');
  const me=await portalApiGet<{organizationId:number;role:string;clinicIds:readonly number[]}>('/dashboard/me');
  if(!Number.isSafeInteger(me.organizationId))throw new Error('Bill recovery scope is unavailable.');
  const prefix=`money-${kind}-operation:${actor}:`;
  const key=prefix+JSON.stringify([me.organizationId,me.role,[...me.clinicIds].sort((a,b)=>a-b)]);
  for(let i=0;i<window.sessionStorage.length;i++) {
    const other=window.sessionStorage.key(i);
    if(other?.startsWith(prefix) && other!==key && window.sessionStorage.getItem(other))throw new Error('An unconfirmed bill change belongs to another session scope. Restore that scope to resolve it.');
  }
  return new PendingBillMutationStore(window.sessionStorage,key);
}
async function send<T>(store: PendingBillMutationStore,pending: PendingBillMutation): Promise<T> {
  try {
    const creating=pending.path==='/patient-bills';
    const result=await portalApiPost<T>(pending.path,{...pending.payload,...(creating?{requestId:pending.operationId}:{operationId:pending.operationId})});
    const wire=result as {bill?:{id?:unknown;appointmentId?:unknown;clinicId?:unknown;status?:unknown;items?:unknown}};
    if(!wire?.bill || (creating ? (!Number.isSafeInteger(wire.bill.id) || Number(wire.bill.id)<=0 || wire.bill.appointmentId!==pending.payload.appointmentId || wire.bill.clinicId!==pending.payload.clinicId) : wire.bill.id!==Number(pending.path.split('/')[2])) || !Array.isArray(wire.bill.items) ||
      !['draft','finalized','cancelled'].includes(String(wire.bill.status)))throw new Error('The saved bill could not be confirmed. Check the original change.');
    store.complete(pending.operationId);
    return result;
  } catch(error) {
    // Only definitive pre-commit refusals may release the retained intent.
    // Generic HTTP500, timeout and malformed success remain unknown.
    if(error instanceof PortalApiError && ['VALIDATION_FAILED','BILL_CHANGE_NOT_SAVED','bill_state_changed','bill_line_price_not_set'].includes(error.code??''))store.complete(pending.operationId);
    throw error;
  }
}
export async function postBillMutation<T>(path: string,payload: Record<string,unknown>): Promise<T> {
  const store=await currentStore();
  return send<T>(store,store.begin(path,payload));
}
export async function retryPendingBillMutation<T>(billIds: readonly number[], appointmentId?: number): Promise<T | null> {
  const store=await currentStore();const pending=store.read();
  if(pending && (pending.path==='/patient-bills' ? pending.payload.appointmentId!==appointmentId : !billIds.includes(Number(pending.path.split('/')[2]))))throw new Error('Open the original bill to check its unconfirmed change.');
  return pending?send<T>(store,pending):null;
}
export async function pendingBillMutationId(appointmentId?: number): Promise<number | null> {
  const pending=(await currentStore()).read();return pending?(pending.path==='/patient-bills'?(pending.payload.appointmentId===appointmentId?0:-Number(pending.payload.appointmentId)):Number(pending.path.split('/')[2])):null;
}

interface VoidReply { operationId: string; auditId: number; payment: { id: number; voidedAt: string | null; voidReason: string | null } }
async function sendVoid<T extends VoidReply>(store: PendingBillMutationStore,pending: PendingBillMutation): Promise<T> {
  try {
    const result=await portalApiPost<T>(pending.path,{...pending.payload,operationId:pending.operationId});
    if(result?.operationId!==pending.operationId || !Number.isSafeInteger(result.auditId) || result.auditId<=0 ||
      result.payment?.id!==Number(pending.path.split('/')[2]) || !result.payment.voidedAt ||
      result.payment.voidReason!==pending.payload.reason)throw new Error('The payment correction could not be confirmed. Check the original correction.');
    store.complete(pending.operationId);
    return result;
  } catch(error) {
    if(error instanceof PortalApiError && ['VALIDATION_FAILED','PAYMENT_NOT_SAVED','payment_already_voided'].includes(error.code??''))store.complete(pending.operationId);
    throw error;
  }
}
export async function postPaymentVoid<T extends VoidReply>(paymentId: number,reason: string): Promise<T> {
  const store=await currentStore('void');
  return sendVoid<T>(store,store.begin(`/patient-payments/${paymentId}/void`,{reason}));
}
export async function pendingPaymentVoidId(): Promise<number | null> {
  const pending=(await currentStore('void')).read();return pending?Number(pending.path.split('/')[2]):null;
}
export async function retryPendingPaymentVoid<T extends VoidReply>(): Promise<T | null> {
  const store=await currentStore('void');const pending=store.read();
  return pending?sendVoid<T>(store,pending):null;
}
